import { keepPreviousData } from '@tanstack/react-query';
import type { DaySlotsWithStatus } from '@web-app/utils/scheduling/day-slot-status';
import type { PickerView } from '@web-app/utils/scheduling/picker-period';
import { trpcClientUnbatched } from '@web-app/utils/trpc';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';

import {
  type AppointmentSubject,
  currentRouteAtom,
  drawerOpenAtom,
} from './schedulingDrawerAtoms';

// ============================================================================
// Constants
// ============================================================================

const STALE_TIME = 2 * 60 * 1000; // 2 min

export const SCHEDULING_QUERY_KEYS = {
  upcomingAppointments: 'scheduling.upcomingAppointments',
  appointmentTypes: 'scheduling.appointmentTypes',
  availableSlots: 'scheduling.availableSlots',
  bookableDaySlots: 'scheduling.bookableDaySlots',
  daySlotsWithStatus: 'scheduling.daySlotsWithStatus',
} as const;

/**
 * Read-through bridge from the canonical `useProviderTimezone` tRPC query to
 * Jotai slot queries. This atom never fetches or resolves a timezone itself.
 */
export const providerTimezoneAtom = atom<string | null>(null);

// ============================================================================
// Selection State
// ============================================================================

export const selectedDateAtom = atom<string | null>(null); // YYYY-MM-DD
export const selectedTimeSlotAtom = atom<string | null>(null); // ISO datetime
export const selectedAppointmentTypeIdAtom = atom<string | null>(null);

/**
 * Whether the availability-aware picker hides times outside the provider's
 * hours. Presentation only — `bookableDaySlotsQueryAtom` still decides whether
 * a time can be confirmed, so turning this off never makes a time bookable.
 */
export const onlyShowInsideAvailabilityAtom = atom(true);

/** Which of the picker's Day / Week / Month views is on show. */
export const viewModeAtom = atom<PickerView>('week');

/**
 * Tracks the visible calendar month as a wall-clock Date carrier.
 * It stays empty until the provider-local initial date is known so the first
 * slot query cannot accidentally use the browser's month.
 */
export const calendarMonthAtom = atom<Date | null>(null);

// ============================================================================
// Query Atoms
// ============================================================================

/** Fetches appointment types when the drawer is open. */
export const appointmentTypesQueryAtom = atomWithQuery((get) => {
  const isOpen = get(drawerOpenAtom);
  return {
    queryKey: [SCHEDULING_QUERY_KEYS.appointmentTypes],
    queryFn: () => trpcClientUnbatched.scheduling.getAppointmentTypes.query(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
});

/**
 * Resolves to the user's explicit selection, or a sensible default once
 * appointment types have loaded. For reschedule, defaults to the existing
 * appointment's type; otherwise falls back to the 60-min type (then first).
 */
export const effectiveAppointmentTypeIdAtom = atom((get) => {
  const selected = get(selectedAppointmentTypeIdAtom);
  if (selected) return selected;
  const types = get(appointmentTypesQueryAtom).data ?? [];
  if (types.length === 0) return null;

  // Only the booking (appointment/create) route carries an existing appointment.
  const subject = get(currentRouteAtom)?.subject as
    | AppointmentSubject
    | undefined;
  const existing = subject?.existingAppointment;
  if (existing) {
    const match = types.find((t) => t.id === existing.appointmentType.id);
    if (match) return match.id;
  }

  const sixtyMin = types.find((t) => t.lengthMinutes === 60);
  return sixtyMin?.id ?? types[0].id;
});

/** Fetches available slots for the effective appointment type and calendar month. */
export const availableSlotsQueryAtom = atomWithQuery((get) => {
  const appointmentTypeId = get(effectiveAppointmentTypeIdAtom);
  const calendarMonth = get(calendarMonthAtom);
  const timezone = get(providerTimezoneAtom);
  const startDate = calendarMonth
    ? format(startOfMonth(calendarMonth), 'yyyy-MM-dd')
    : '';
  const endDate = calendarMonth
    ? format(endOfMonth(calendarMonth), 'yyyy-MM-dd')
    : '';

  return {
    queryKey: [
      SCHEDULING_QUERY_KEYS.availableSlots,
      appointmentTypeId,
      startDate,
      endDate,
      timezone,
    ],
    // Wait for profile timezone — slots must be in Healthie provider TZ, not browser.
    queryFn: () =>
      trpcClientUnbatched.scheduling.getAvailableSlots.query({
        appointmentTypeId: appointmentTypeId ?? '',
        startDate,
        endDate,
        timezone: timezone ?? undefined,
      }),
    enabled: !!appointmentTypeId && !!timezone && !!calendarMonth,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: 1,
  };
});

/**
 * The appointment being rescheduled (the booking route carries it), else null.
 * It is moved before the new one is made, so it never counts as an overlap.
 */
export const rescheduledAppointmentIdAtom = atom((get) => {
  const subject = get(currentRouteAtom)?.subject as
    | AppointmentSubject
    | undefined;
  return subject?.existingAppointment?.id ?? null;
});

/** Length of the effective appointment type; 60 until types have loaded. */
export const selectedLengthMinutesAtom = atom((get) => {
  const appointmentTypeId = get(effectiveAppointmentTypeIdAtom);
  const types = get(appointmentTypesQueryAtom).data ?? [];
  return types.find((t) => t.id === appointmentTypeId)?.lengthMinutes ?? 60;
});

/** Fetches bookable start times for the selected day (full day minus calendar busy). */
export const bookableDaySlotsQueryAtom = atomWithQuery((get) => {
  const isOpen = get(drawerOpenAtom);
  const selectedDate = get(selectedDateAtom);
  const appointmentTypeId = get(effectiveAppointmentTypeIdAtom);
  const lengthMinutes = get(selectedLengthMinutesAtom);
  const timezone = get(providerTimezoneAtom);

  return {
    queryKey: [
      SCHEDULING_QUERY_KEYS.bookableDaySlots,
      selectedDate,
      appointmentTypeId,
      lengthMinutes,
      timezone,
    ],
    queryFn: (): Promise<string[]> => {
      if (!selectedDate) return Promise.resolve([]);
      return trpcClientUnbatched.scheduling.getBookableDaySlots.query({
        date: selectedDate,
        lengthMinutes,
        timezone: timezone ?? undefined,
      });
    },
    enabled: isOpen && !!selectedDate && !!appointmentTypeId && !!timezone,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: 1,
  };
});

/**
 * The selected day's remaining slots labelled open / outsideAvailability /
 * booked, for the availability-aware picker. Not read by the original picker,
 * so it never fetches there.
 */
export const daySlotsWithStatusQueryAtom = atomWithQuery((get) => {
  const isOpen = get(drawerOpenAtom);
  const selectedDate = get(selectedDateAtom);
  const appointmentTypeId = get(effectiveAppointmentTypeIdAtom);
  const lengthMinutes = get(selectedLengthMinutesAtom);
  const timezone = get(providerTimezoneAtom);
  const excludeAppointmentId = get(rescheduledAppointmentIdAtom) ?? undefined;

  return {
    queryKey: [
      SCHEDULING_QUERY_KEYS.daySlotsWithStatus,
      selectedDate,
      appointmentTypeId,
      lengthMinutes,
      timezone,
      excludeAppointmentId,
    ],
    queryFn: (): Promise<DaySlotsWithStatus> =>
      trpcClientUnbatched.scheduling.getDaySlotsWithStatus.query({
        date: selectedDate ?? '',
        lengthMinutes,
        timezone: timezone ?? '',
        excludeAppointmentId,
      }),
    enabled: isOpen && !!selectedDate && !!appointmentTypeId && !!timezone,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: 1,
  };
});

// ============================================================================
// Derived Atoms
// ============================================================================

/** Groups flat slot array by date for calendar highlighting and time display. */
export const availableSlotsByDateAtom = atom((get) => {
  const queryResult = get(availableSlotsQueryAtom);
  const slots = queryResult.data ?? [];

  const byDate = new Map<string, string[]>();
  for (const slot of slots) {
    const dateKey = slot.date.substring(0, 10);
    const existing = byDate.get(dateKey) ?? [];
    existing.push(slot.date);
    byDate.set(dateKey, existing);
  }
  return byDate;
});

/**
 * Dates that have availability (for calendar day highlighting).
 *
 * S-2: Uses selectAtom with a shallow equality check to return a stable
 * reference when the set of dates hasn't changed. Without this,
 * [...keys()] creates a new array on every upstream change, causing
 * unnecessary re-renders in subscribers (jotai uses Object.is for equality).
 *
 * selectAtom tracks the previous value per-store internally, so this is
 * safe even if multiple Jotai stores exist.
 */
const shallowStringArrayEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const availableDatesAtom = selectAtom(
  availableSlotsByDateAtom,
  (byDate) => [...byDate.keys()],
  shallowStringArrayEqual,
);

/** Time slots for the selected date (full day minus busy).
 * Returns [] for placeholder data from a different date or errors — prevents stale/wrong slots. */
export const selectedDateTimeSlotsAtom = atom((get): string[] => {
  const selectedDate = get(selectedDateAtom);
  if (!selectedDate) return [];
  const query = get(bookableDaySlotsQueryAtom);
  if (query.isPlaceholderData || query.isError) return [];
  return query.data ?? [];
});

const NO_DAY_SLOTS: DaySlotsWithStatus = { slots: [], windows: [] };

/**
 * What the availability-aware time list renders. Empty for placeholder data
 * from another date or on error, so a stale or wrong day is never shown.
 */
export const daySlotsViewAtom = atom((get) => {
  const query = get(daySlotsWithStatusQueryAtom);
  const stale = !get(selectedDateAtom) || query.isPlaceholderData;
  const { slots, windows } =
    stale || query.isError ? NO_DAY_SLOTS : (query.data ?? NO_DAY_SLOTS);
  return {
    details: slots,
    windows,
    isLoading: query.isPending || query.isFetching,
    isError: query.isError,
  };
});

/** The chosen time's status in the day's list, and whether that list is still loading. */
export const selectedSlotViewAtom = atom((get) => {
  const { details, isLoading } = get(daySlotsViewAtom);
  const slot = get(selectedTimeSlotAtom);
  return {
    status: slot ? details.find((d) => d.slot === slot)?.status : undefined,
    isLoading,
  };
});

/** True while bookable slots for the selected day are loading. */
export const isBookableSlotsLoadingAtom = atom((get) => {
  const query = get(bookableDaySlotsQueryAtom);
  return query.isPending || query.isFetching;
});

/** Write atom that resets all selection atoms (called on drawer close). */
export const resetSchedulingStateAtom = atom(null, (_get, set) => {
  set(selectedDateAtom, null);
  set(selectedTimeSlotAtom, null);
  set(selectedAppointmentTypeIdAtom, null);
  set(calendarMonthAtom, null);
  set(onlyShowInsideAvailabilityAtom, true);
  set(viewModeAtom, 'week');
});
